// The live agent / Knowledge-Bank graph (admin).
// Backend: GET /api/admin/agent-console/topology, derived from the registries at
// call time — so this page cannot drift from what the runtime actually mounts.
// Every hand-drawn version of this map was wrong within a week; that is why it
// is read off the code instead of maintained.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cytoscape, {
  type Core,
  type ElementDefinition,
  type EventObject,
} from 'cytoscape';
import dagre from 'cytoscape-dagre';

import {
  deleteMountOverride,
  fetchAgentTopology,
  filterToMerlin,
  countWriteEdges,
  putMountOverride,
  type AgentNode,
  type AgentTopology,
  type BankNode,
  type OverrideAccess,
} from '../services/agentTopology';
import styles from './AgentTopologyPage.module.css';

cytoscape.use(dagre);

const KIND_TONE: Record<string, { bg: string; ink: string }> = {
  picker: { bg: '#2563eb', ink: '#fff' },
  sub_profile: { bg: '#7c3aed', ink: '#fff' },
  door: { bg: '#0891b2', ink: '#fff' },
  headless: { bg: '#ca8a04', ink: '#fff' },
  playbook: { bg: '#16a34a', ink: '#fff' },
  domain: { bg: '#059669', ink: '#fff' },
  surface: { bg: '#94a3b8', ink: '#0f172a' },
};

const LAYOUT = {
  name: 'dagre',
  rankDir: 'LR',
  nodeSep: 18,
  rankSep: 150,
  edgeSep: 10,
} as unknown as cytoscape.LayoutOptions;

const STYLE = [
  {
    selector: 'node',
    style: {
      label: 'data(label)',
      'text-wrap': 'wrap',
      'text-max-width': 160,
      'text-valign': 'center',
      'text-halign': 'center',
      'font-size': 11,
      'font-weight': 600,
      'line-height': 1.2,
      width: 190,
      height: 54,
      shape: 'round-rectangle',
      'background-color': 'data(bg)',
      color: 'data(ink)',
      'border-width': 0,
    },
  },
  { selector: 'node[type="bank"]', style: { width: 176, height: 46, 'font-size': 10.5 } },
  { selector: 'node[type="surface"]', style: { shape: 'round-diamond', 'font-size': 10.5 } },
  { selector: 'node.sel', style: { 'border-width': 3, 'border-color': '#111827' } },
  {
    selector: 'edge',
    style: {
      width: 1.6,
      'curve-style': 'bezier',
      'target-arrow-shape': 'triangle',
      'arrow-scale': 0.9,
      'line-color': '#cbd5e1',
      'target-arrow-color': '#cbd5e1',
    },
  },
  {
    selector: 'edge[kind="derives"]',
    style: { width: 2.6, 'line-color': '#475569', 'target-arrow-color': '#475569' },
  },
  {
    selector: 'edge[kind="runs"]',
    style: {
      width: 2,
      'line-style': 'dotted',
      'line-color': '#64748b',
      'target-arrow-color': '#64748b',
    },
  },
  {
    selector: 'edge[kind="write"]',
    style: { width: 2.2, 'line-color': '#16a34a', 'target-arrow-color': '#16a34a' },
  },
  { selector: 'edge[kind="read"]', style: { 'line-style': 'dashed' } },
] as unknown as cytoscape.Stylesheet[];

function buildElements(topology: AgentTopology, showReads: boolean): ElementDefinition[] {
  const els: ElementDefinition[] = [];
  const agentIds = new Set(topology.agents.map((a) => a.id));
  // Only draw a bank that something in the CURRENT view actually reaches —
  // otherwise the Merlin filter leaves banks floating with no edges.
  const reached = new Set(topology.agents.flatMap((a) => a.banks.map((b) => b.bank)));

  for (const agent of topology.agents) {
    const tone = KIND_TONE[agent.kind];
    els.push({
      data: {
        id: `agent:${agent.id}`,
        label: `${agent.name}\n(${agent.id})`,
        bg: tone.bg,
        ink: tone.ink,
        type: 'agent',
        ref: agent.id,
      },
    });
  }
  for (const bank of topology.banks) {
    if (!reached.has(bank.id)) continue;
    const tone = KIND_TONE[bank.kind];
    els.push({
      data: {
        id: `bank:${bank.id}`,
        label: bank.label,
        bg: tone.bg,
        ink: tone.ink,
        type: 'bank',
        ref: bank.id,
      },
    });
  }
  for (const surface of topology.surfaces) {
    els.push({
      data: {
        id: `surface:${surface.id}`,
        label: `${surface.label}\n(surface)`,
        bg: KIND_TONE.surface.bg,
        ink: KIND_TONE.surface.ink,
        type: 'surface',
        ref: surface.id,
      },
    });
    // A surface points at the agent behind the window. A retired tag has none,
    // so it is drawn as an island rather than pointed at a stand-in.
    if (surface.runs && agentIds.has(surface.runs)) {
      els.push({
        data: {
          id: `runs:${surface.id}`,
          source: `surface:${surface.id}`,
          target: `agent:${surface.runs}`,
          kind: 'runs',
        },
      });
    }
  }
  for (const agent of topology.agents) {
    const parent = agent.inheritance.parent;
    if (parent && agentIds.has(parent)) {
      els.push({
        data: {
          id: `derive:${agent.id}`,
          source: `agent:${parent}`,
          target: `agent:${agent.id}`,
          kind: 'derives',
        },
      });
    }
    for (const mount of agent.banks) {
      const write = mount.access === 'read+write';
      if (!write && !showReads) continue;
      if (!reached.has(mount.bank)) continue;
      els.push({
        data: {
          id: `mount:${agent.id}:${mount.bank}:${mount.access}`,
          source: `agent:${agent.id}`,
          target: `bank:${mount.bank}`,
          kind: write ? 'write' : 'read',
        },
      });
    }
  }
  return els;
}

type Selection =
  | { type: 'agent'; agent: AgentNode }
  | { type: 'bank'; bank: BankNode }
  | { type: 'surface'; id: string }
  | null;

export function AgentTopologyPage() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const cyRef = useRef<Core | null>(null);
  const [topology, setTopology] = useState<AgentTopology | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [merlinOnly, setMerlinOnly] = useState(false);
  const [showReads, setShowReads] = useState(true);
  const [selected, setSelected] = useState<Selection>(null);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Re-render from what the SERVER now believes (the mutation endpoints return
  // the updated graph), and keep the panel on the same node so the admin sees
  // their edit land rather than the selection vanishing.
  const applyTopology = useCallback((data: AgentTopology) => {
    setTopology(data);
    setSelected((sel) => {
      if (sel?.type === 'agent') {
        const agent = data.agents.find((a) => a.id === sel.agent.id);
        return agent ? { type: 'agent', agent } : null;
      }
      if (sel?.type === 'bank') {
        const bank = data.banks.find((b) => b.id === sel.bank.id);
        return bank ? { type: 'bank', bank } : null;
      }
      return sel;
    });
  }, []);

  const changeAccess = useCallback(
    async (profileId: string, bankId: string, access: OverrideAccess) => {
      setSaving(true);
      setEditError(null);
      try {
        applyTopology(await putMountOverride(profileId, bankId, access));
      } catch (err) {
        const detail = (err as { response?: { data?: { detail?: unknown } } })
          ?.response?.data?.detail;
        setEditError(typeof detail === 'string' ? detail : 'The change was not saved.');
      } finally {
        setSaving(false);
      }
    },
    [applyTopology],
  );

  const revertOverride = useCallback(
    async (profileId: string, bankId: string) => {
      setSaving(true);
      setEditError(null);
      try {
        applyTopology(await deleteMountOverride(profileId, bankId));
      } catch {
        setEditError('The revert was not saved.');
      } finally {
        setSaving(false);
      }
    },
    [applyTopology],
  );

  useEffect(() => {
    let cancelled = false;
    fetchAgentTopology()
      .then((data) => {
        if (!cancelled) setTopology(data);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load the topology.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const view = useMemo(
    () => (topology && merlinOnly ? filterToMerlin(topology) : topology),
    [topology, merlinOnly],
  );


  useEffect(() => {
    if (!view || !containerRef.current) return;
    cyRef.current?.destroy();
    const cy = cytoscape({
      container: containerRef.current,
      elements: buildElements(view, showReads),
      style: STYLE,
      layout: LAYOUT,
    });
    // Resolve a clicked node back to its record. Defined inside the effect so it
    // closes over the CURRENT view — the graph is rebuilt whenever that changes,
    // so there is no stale-closure window to guard against.
    const select = (type: string, ref: string) => {
      if (type === 'agent') {
        const agent = view.agents.find((a) => a.id === ref);
        setSelected(agent ? { type: 'agent', agent } : null);
      } else if (type === 'bank') {
        const bank = view.banks.find((b) => b.id === ref);
        setSelected(bank ? { type: 'bank', bank } : null);
      } else {
        setSelected({ type: 'surface', id: ref });
      }
    };
    cy.on('tap', 'node', (ev: EventObject) => {
      cy.nodes().removeClass('sel');
      ev.target.addClass('sel');
      select(ev.target.data('type'), ev.target.data('ref'));
    });
    cy.on('tap', (ev: EventObject) => {
      if (ev.target === cy) {
        cy.nodes().removeClass('sel');
        setSelected(null);
      }
    });
    cyRef.current = cy;
    return () => {
      cy.destroy();
      cyRef.current = null;
    };
  }, [view, showReads]);

  const refit = useCallback(() => cyRef.current?.fit(undefined, 24), []);

  if (error) {
    return (
      <div className={styles.page}>
        <div className={styles.error}>{error}</div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1>Agent Topology</h1>
        <p>
          Every agent, the Knowledge Banks it reaches, and — for derived agents — what it
          inherits and what it overrides. Read off the live registries on each request, so
          it cannot drift from what the runtime mounts.
        </p>
      </div>

      <div className={styles.toolbar}>
        <label>
          <input
            type="checkbox"
            checked={merlinOnly}
            onChange={(e) => {
              // Filtering can remove the node the panel is describing, and a
              // panel describing something no longer on the canvas is worse
              // than an empty one — it reads as a selection you cannot find.
              setSelected(null);
              setMerlinOnly(e.target.checked);
            }}
          />
          Merlin agents only
        </label>
        <label>
          <input
            type="checkbox"
            checked={showReads}
            onChange={(e) => setShowReads(e.target.checked)}
          />
          show read-only mounts
        </label>
        <span className={styles.spacer} />
        {view && (
          <span>
            {view.agents.length} agents · {countWriteEdges(view)} write edges
          </span>
        )}
        <button type="button" className={styles.btn} onClick={refit}>
          re-fit
        </button>
      </div>

      <div className={styles.split}>
        <div ref={containerRef} className={styles.canvas} />
        <aside className={styles.detail}>
          <DetailPanel
            selected={selected}
            topology={view}
            saving={saving}
            editError={editError}
            onChangeAccess={changeAccess}
            onRevert={revertOverride}
          />
        </aside>
      </div>

      <div className={styles.legend}>
        <span>
          <i className={styles.dot} style={{ background: KIND_TONE.picker.bg }} />
          agent (picker)
        </span>
        <span>
          <i className={styles.dot} style={{ background: KIND_TONE.sub_profile.bg }} />
          sub-profile
        </span>
        <span>
          <i className={styles.dot} style={{ background: KIND_TONE.door.bg }} />
          door
        </span>
        <span>
          <i className={styles.dot} style={{ background: KIND_TONE.headless.bg }} />
          headless
        </span>
        <span>
          <i className={styles.dot} style={{ background: KIND_TONE.surface.bg }} />
          surface (not an agent)
        </span>
        <span>
          <i className={styles.dot} style={{ background: KIND_TONE.playbook.bg }} />
          playbook bank
        </span>
        <span>
          <i className={styles.dot} style={{ background: KIND_TONE.domain.bg }} />
          domain bank
        </span>
        <span>Solid grey = derives from · green = write · dashed = read-only</span>
      </div>
    </div>
  );
}

function DetailPanel({
  selected,
  topology,
  saving,
  editError,
  onChangeAccess,
  onRevert,
}: {
  selected: Selection;
  topology: AgentTopology | null;
  saving: boolean;
  editError: string | null;
  onChangeAccess: (profileId: string, bankId: string, access: OverrideAccess) => void;
  onRevert: (profileId: string, bankId: string) => void;
}) {
  if (!selected) {
    return (
      <>
        <h2>Select a node</h2>
        <p className={styles.muted}>
          Click an agent to see where it runs, which banks it reaches, and exactly which
          fields it inherits from or overrides on its parent.
        </p>
      </>
    );
  }

  if (selected.type === 'surface') {
    const surface = topology?.surfaces.find((s) => s.id === selected.id);
    if (!surface) return <h2>{selected.id}</h2>;
    return (
      <>
        <h2>{surface.label}</h2>
        <p className={styles.muted}>
          <code className={styles.mono}>{surface.id}</code>{' '}
          {surface.retired ? (
            <span className={`${styles.pill} ${styles.pillWarn}`}>retired</span>
          ) : (
            <span className={`${styles.pill} ${styles.pillKind}`}>surface</span>
          )}
        </p>
        <p className={styles.muted}>
          {surface.retired
            ? 'A conversation tag whose agent no longer exists. Its threads stay readable; the turn route refuses to run them rather than substituting another agent.'
            : `Not an agent — a window onto ${surface.runs}. Threads carry this tag so the door can scope its own history, while the turn runs that agent.`}
        </p>
      </>
    );
  }

  if (selected.type === 'bank') {
    const bank = selected.bank;
    return (
      <>
        <h2>{bank.label}</h2>
        <p className={styles.muted}>
          <code className={styles.mono}>{bank.id}</code>{' '}
          <span className={`${styles.pill} ${styles.pillKind}`}>{bank.kind}</span>
          {!bank.provisioned && (
            <span className={`${styles.pill} ${styles.pillWarn}`}>unprovisioned</span>
          )}
        </p>
        <h3>Writers</h3>
        {bank.writers.length ? (
          bank.writers.map((id) => (
            <div key={id} className={styles.row}>
              <span>{id}</span>
              <span className={`${styles.pill} ${styles.pillWrite}`}>read+write</span>
            </div>
          ))
        ) : (
          <p className={styles.muted}>No Agent Console agent writes here.</p>
        )}
        <h3>Readers</h3>
        {bank.readers.length ? (
          bank.readers.map((id) => (
            <div key={id} className={styles.row}>
              <span>{id}</span>
              <span className={`${styles.pill} ${styles.pillRead}`}>read-only</span>
            </div>
          ))
        ) : (
          <p className={styles.muted}>No Agent Console agent reads here.</p>
        )}
        <p className={styles.muted} style={{ marginTop: 12 }}>
          Agent Console agents only — a bank may also be reached by another agent stack.
        </p>
      </>
    );
  }

  const agent = selected.agent;
  return (
    <>
      <h2>{agent.name}</h2>
      <p className={styles.muted}>
        <code className={styles.mono}>{agent.id}</code>{' '}
        <span className={`${styles.pill} ${styles.pillKind}`}>
          {agent.kind.replace('_', '-')}
        </span>
        <span className={`${styles.pill} ${agent.writes ? styles.pillWrite : styles.pillRead}`}>
          {agent.writes ? 'writes' : 'read-only'}
        </span>
        {agent.reskinned && (
          <span className={`${styles.pill} ${styles.pillKind}`}>re-skinned</span>
        )}
      </p>
      <p className={styles.muted}>{agent.description}</p>
      <p className={styles.muted}>{agent.kind_note}</p>

      <h3>Where it runs</h3>
      {agent.entry_points.length ? (
        agent.entry_points.map((entry) => (
          <div key={`${entry.kind}:${entry.label}`} className={styles.entry}>
            <strong>{entry.label}</strong> <span className={styles.mono}>({entry.kind})</span>
            <div>{entry.detail}</div>
            <div className={styles.mono}>{entry.source}</div>
          </div>
        ))
      ) : (
        <p className={styles.muted}>Nothing reaches this agent.</p>
      )}

      <h3>Knowledge Banks</h3>
      <p className={styles.muted}>
        Editable — a change is stored as an override on the code baseline and the
        agent&apos;s NEXT turn mounts it. Matching the baseline reverts.
      </p>
      {editError && <p className={styles.editError}>{editError}</p>}
      {agent.banks.length ? (
        agent.banks.map((mount) => {
          const ownPlaybook = mount.bank === `playbook:${agent.id}`;
          return (
            <div key={mount.bank} className={styles.row}>
              <span>
                {mount.label}
                {mount.overridden && (
                  <>
                    {' '}
                    <span className={`${styles.pill} ${styles.pillWarn}`}>
                      overridden · code: {mount.code_access}
                    </span>
                  </>
                )}
                <br />
                <code className={styles.mono}>mcp__{mount.mount}__*</code>
              </span>
              {ownPlaybook ? (
                <span
                  className={`${styles.pill} ${styles.pillWrite}`}
                  title="An agent's own playbook is not overridable — every agent keeps its own operating memory."
                >
                  read+write
                </span>
              ) : (
                <span className={styles.mountControls}>
                  <select
                    aria-label={`access for ${mount.label}`}
                    disabled={saving}
                    value={mount.access}
                    onChange={(e) =>
                      onChangeAccess(
                        agent.id,
                        mount.bank,
                        e.target.value === 'read+write'
                          ? 'read_write'
                          : e.target.value === 'read-only'
                            ? 'read_only'
                            : 'removed',
                      )
                    }
                  >
                    {mount.kind === 'domain' && (
                      <option value="read+write">read+write</option>
                    )}
                    <option value="read-only">read-only</option>
                    <option value="removed">removed</option>
                  </select>
                  {mount.overridden && (
                    <button
                      type="button"
                      className={styles.btn}
                      disabled={saving}
                      onClick={() => onRevert(agent.id, mount.bank)}
                    >
                      revert
                    </button>
                  )}
                </span>
              )}
            </div>
          );
        })
      ) : (
        <p className={styles.muted}>None.</p>
      )}
      {(agent.mount_overrides ?? [])
        .filter((over) => over.access === 'removed')
        .map((over) => (
          <div key={over.bank} className={styles.row}>
            <span>
              {over.label}{' '}
              <span className={`${styles.pill} ${styles.pillWarn}`}>
                removed · code: {over.code_access}
              </span>
            </span>
            <button
              type="button"
              className={styles.btn}
              disabled={saving}
              onClick={() => onRevert(agent.id, over.bank)}
            >
              revert
            </button>
          </div>
        ))}
      <AddMount
        agent={agent}
        topology={topology}
        saving={saving}
        onChangeAccess={onChangeAccess}
      />

      {agent.inheritance.parent && (
        <>
          <h3>Derived from {agent.inheritance.parent}</h3>
          <p className={styles.muted}>
            Inherits {agent.inheritance.inherited.length} capability fields unchanged —
            derivation copies everything, so the declaration lists only the differences.
            Overrides {agent.inheritance.overridden.length}:
          </p>
          {agent.inheritance.overridden.map((over) => (
            <div key={over.field} className={styles.row}>
              <span>
                <code className={styles.mono}>{over.field}</code>
                <br />
                <span className={styles.mono}>{JSON.stringify(over.value)}</span>
              </span>
              <span className={styles.mono} style={{ opacity: 0.6 }}>
                was {JSON.stringify(over.parent)}
              </span>
            </div>
          ))}
        </>
      )}

      <h3>Tools ({agent.tools.length})</h3>
      <p className={styles.mono}>{agent.tools.join(' ') || 'none'}</p>
    </>
  );
}

function AddMount({
  agent,
  topology,
  saving,
  onChangeAccess,
}: {
  agent: AgentNode;
  topology: AgentTopology | null;
  saving: boolean;
  onChangeAccess: (profileId: string, bankId: string, access: OverrideAccess) => void;
}) {
  const [bankId, setBankId] = useState('');
  const [access, setAccess] = useState<OverrideAccess>('read_only');
  const mounted = new Set(agent.banks.map((b) => b.bank));
  const candidates = (topology?.mountable_banks ?? []).filter(
    (b) => !mounted.has(b.id) && b.id !== `playbook:${agent.id}`,
  );
  if (!candidates.length) return null;
  const chosen = candidates.find((b) => b.id === bankId);
  return (
    <div className={styles.addMount}>
      <select
        aria-label="bank to mount"
        disabled={saving}
        value={bankId}
        onChange={(e) => {
          setBankId(e.target.value);
          // A playbook can only ever be read: snap the access back.
          if (e.target.value.startsWith('playbook:')) setAccess('read_only');
        }}
      >
        <option value="">+ mount a bank…</option>
        {candidates.map((b) => (
          <option key={b.id} value={b.id}>
            {b.label} ({b.kind})
          </option>
        ))}
      </select>
      {chosen && (
        <>
          <select
            aria-label="access for the new mount"
            disabled={saving}
            value={access}
            onChange={(e) => setAccess(e.target.value as OverrideAccess)}
          >
            <option value="read_only">read-only</option>
            {chosen.kind === 'domain' && (
              <option value="read_write">read+write</option>
            )}
          </select>
          <button
            type="button"
            className={styles.btn}
            disabled={saving}
            onClick={() => {
              onChangeAccess(agent.id, bankId, access);
              setBankId('');
            }}
          >
            mount
          </button>
        </>
      )}
    </div>
  );
}
